import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants/pagination';

import { vestigasApi } from '~/services/kyClient';

import { AcceptStateCalculatorObject } from '~/models/acceptState';
import { DeliveryNoteObject } from '~/models/deliveries/DeliveryNote';

import { toSnakeCase } from '@vestigas/utils';

import { queryKeysDeliveryNote } from './queryKeys';
import {
  DELIVERY_NOTE_FILTER_NAMES,
  zodFilterConfigSchema,
} from './zodDeliveryNoteFiltersSchema';

const FILTER_ENTITY_NAMES = DELIVERY_NOTE_FILTER_NAMES.filter(
  (name) => !['category', 'is_mapped'].includes(name),
);

type FilterEntity = (typeof FILTER_ENTITY_NAMES)[number];

const zodFilterSuggestionsParamsSchema = z
  .object({
    entity: z.enum(FILTER_ENTITY_NAMES),
    filterConfig: zodFilterConfigSchema,
    limit: z
      .number()
      .gte(1)
      .lte(200)
      .optional()
      .prefault(PAGINATION_PAGE_SIZE_DEFAULT),
    offset: z.number().nonnegative().optional().prefault(0),
    searchValue: z.string().optional(),
  })
  .transform((value) => {
    // Remove the current filter entity from all filter clauses so values aren't filtered out that should be suggested.
    const cleanedFilterConfig = {
      filterConfig: {
        ...value.filterConfig,
        filterGroups: value.filterConfig.filterGroups
          .map((group) => ({
            ...group,
            filterClauses: group.filterClauses.filter(
              ({ name }) => name !== value.entity,
            ),
          }))
          // Remove filter groups with no filter clauses.
          .filter((group) => group.filterClauses.length > 0),
      },
    };

    return {
      ...value,
      ...cleanedFilterConfig,
    };
  });

type FilterSuggestionsParams = z.infer<typeof zodFilterSuggestionsParamsSchema>;

type FilterSuggestionsResponse = {
  items: string[];
};

/**
 * Fetches possible values for a delivery notes filter from the API.
 * Handles pagination and error cases while enforcing API limits.
 *
 * @param queryParams - Parameters for the filter suggestions query
 * @param queryParams.entity - The entity type to get suggestions for
 * @param queryParams.filterConfig - Optional filter configuration
 * @param queryParams.limit - Maximum number of suggestions to return (max 200)
 * @param queryParams.offset - Pagination offset
 * @param queryParams.searchValue - Optional search term to filter suggestions
 * @returns Promise containing array of suggestion strings
 * @throws Error if API request fails
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Analytics/operation/compute_dln_analytics_suggestions_analytics_suggestions_post
 */
export const fetchFilterSuggestions = async (
  queryParams: FilterSuggestionsParams,
) => {
  switch (
    toSnakeCase(queryParams.entity) as
      | FilterEntity
      | 'accept_state'
      | 'settled_status'
  ) {
    case 'process_state': {
      // Don't query the API - this is only a short list of predefined options.
      return DeliveryNoteObject.getProcessStateOptions();
    }

    case 'accept_state':
    case 'dln_accept_state': {
      // Don't query the API - this is only a short list of predefined options.
      return AcceptStateCalculatorObject.getAcceptStates();
    }

    case 'settled_status': {
      // Don't query the API - this is only a short list of predefined options.
      return ['Ja', 'Nein'];
    }

    default: {
      const response = await vestigasApi
        .post(ENDPOINT.DELIVERY_NOTE.GET_FILTER_SUGGESTIONS(), {
          json: queryParams,
          validate: {
            body: zodFilterSuggestionsParamsSchema,
          },
        })
        .json<FilterSuggestionsResponse>();

      return response.items;
    }
  }
};

/**
 * React Query based custom hook for fetching possible values for a delivery notes filter.
 *
 * @param queryParams - Parameters for the filter suggestions query
 * @param options - Additional options for the useQuery hook
 * @returns UseQueryResult containing an array of suggestion strings
 */
export const useQueryDeliveryNoteFilterSuggestions = (
  queryParams: FilterSuggestionsParams,
  options?: Omit<UseQueryOptions<string[]>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    async queryFn() {
      switch (toSnakeCase(queryParams.entity) as FilterEntity) {
        case 'process_state': {
          // Don't query the API - this is only a short list of predefined options.
          return DeliveryNoteObject.getProcessStateOptions();
        }

        case 'dln_accept_state': {
          // Don't query the API - this is only a short list of predefined options.
          return AcceptStateCalculatorObject.getAcceptStates();
        }

        default: {
          return fetchFilterSuggestions(queryParams);
        }
      }
    },
    queryKey: queryKeysDeliveryNote.getFilterSuggestions(queryParams),
    ...options,
  });
};
